import React, { useEffect, useState, useRef } from 'react';
import './style.scss';

const VersionInfo = () => {
  const versionData = {
    projectVersion: process.env.REACT_APP_PROJECT_VERSION || 'unknown', // 프로젝트 자체 버전
    buildVersion: process.env.REACT_APP_VERSION || 'unknown', // 빌드 버전
    buildDate: process.env.REACT_APP_BUILD_DATE || 'unknown',
    commitSha: process.env.REACT_APP_COMMIT_SHA || 'unknown',
    environment: process.env.REACT_APP_ENV || 'unknown',
    apiBase: process.env.REACT_APP_API_BASE || 'unknown'
  };

  const isDevelopment = process.env.REACT_APP_ENV === 'development';
  const isProduction = process.env.REACT_APP_ENV === 'production';

  // 드래그 앤 드롭 관련 상태
  const [position, setPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const elementRef = useRef(null);

  // 드래그 핸들러
  const handleMouseDown = e => {
    if (e.target.closest('.version-info__toggle')) return; // 토글 버튼 클릭 시 드래그 방지

    setIsDragging(true);
    const rect = elementRef.current.getBoundingClientRect();
    setDragStart({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = e => {
    if (!isDragging) return;

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    // 화면 밖으로 나가지 않도록 제한
    const maxX = window.innerWidth - elementRef.current.offsetWidth;
    const maxY = window.innerHeight - elementRef.current.offsetHeight;

    setPosition({
      x: Math.min(Math.max(0, newX), maxX),
      y: Math.min(Math.max(0, newY), maxY)
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // 터치 이벤트 핸들러 (모바일 지원)
  const handleTouchStart = e => {
    if (e.target.closest('.version-info__toggle')) return;

    const touch = e.touches[0];
    setIsDragging(true);
    const rect = elementRef.current.getBoundingClientRect();
    setDragStart({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
  };

  const handleTouchMove = e => {
    if (!isDragging) return;

    const touch = e.touches[0];
    const newX = touch.clientX - dragStart.x;
    const newY = touch.clientY - dragStart.y;

    const maxX = window.innerWidth - elementRef.current.offsetWidth;
    const maxY = window.innerHeight - elementRef.current.offsetHeight;

    setPosition({
      x: Math.min(Math.max(0, newX), maxX),
      y: Math.min(Math.max(0, newY), maxY)
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, dragStart]);

  useEffect(() => {
    // 운영환경에서는 항상 콘솔에 출력
    if (isProduction) {
      console.group('🚀 NIPA Dreams Admin - Build Information');
      console.log('Project Version:', versionData.projectVersion);
      console.log('Build Version:', versionData.buildVersion);
      console.log('Build Date:', versionData.buildDate);
      console.log('Commit SHA:', versionData.commitSha);
      console.log('Environment:', versionData.environment);
      console.log('API Base URL:', versionData.apiBase);
      console.groupEnd();
    }

    // 개발환경에서도 콘솔에 출력 (추가 정보용)
    if (isDevelopment) {
      console.group('🔧 Development Build Information');
      console.log('Project Version:', versionData.projectVersion);
      console.log('Build Version:', versionData.buildVersion);
      console.log('Build Date:', versionData.buildDate);
      console.log('Commit SHA:', versionData.commitSha);
      console.log('Environment:', versionData.environment);
      console.log('API Base URL:', versionData.apiBase);
      console.groupEnd();
    }
  }, []);

  // 개발환경에서만 화면에 표시
  if (!isDevelopment) {
    return null;
  }

  const getPositionStyle = () => {
    if (position.x !== null && position.y !== null) {
      return {
        left: `${position.x}px`,
        top: `${position.y}px`,
        right: 'auto',
        bottom: 'auto'
      };
    }
    return {};
  };

  return (
    <div
      ref={elementRef}
      className={`version-info ${isDragging ? 'version-info--dragging' : ''}`}
      style={getPositionStyle()}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div className="version-info__header">
        <div className="version-info__title-wrapper">
          <svg className="version-info__drag-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 0l-2 2h1.5v4.5H3V5L1 7l2 2V7.5h4.5V12H6l2 2 2-2H8.5V7.5H13V9l2-2-2-2v1.5H8.5V2H10L8 0z" />
          </svg>
          <span className="version-info__title">🔧 Dev Build Info</span>
        </div>
        <button
          className="version-info__toggle"
          onClick={e => e.target.closest('.version-info').classList.toggle('version-info--collapsed')}
        >
          ▼
        </button>
      </div>
      <div className="version-info__content">
        <div className="version-info__item">
          <span className="version-info__label">Project Ver:</span>
          <span className="version-info__value version-info__value--project">v{versionData.projectVersion}</span>
        </div>
        <div className="version-info__item">
          <span className="version-info__label">Build Ver:</span>
          <span className="version-info__value version-info__value--build">{versionData.buildVersion}</span>
        </div>
        <div className="version-info__item">
          <span className="version-info__label">Build Date:</span>
          <span className="version-info__value">{versionData.buildDate}</span>
        </div>
        <div className="version-info__item">
          <span className="version-info__label">Commit:</span>
          <span className="version-info__value" title={versionData.commitSha}>
            {versionData.commitSha.substring(0, 7)}
          </span>
        </div>
        <div className="version-info__item">
          <span className="version-info__label">Environment:</span>
          <span className="version-info__value version-info__value--env">{versionData.environment}</span>
        </div>
        <div className="version-info__item">
          <span className="version-info__label">API:</span>
          <span className="version-info__value">{versionData.apiBase}</span>
        </div>
      </div>
    </div>
  );
};

export default VersionInfo;
